import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { notification, Form, Row, Col, Spin, Input, Button } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import ConfirmationModal from '../../CustomComponents/ConfirmationModal';
import CustomTypography from '../../CustomComponents/CustomTypography';
import FloatLabel from '../../CustomComponents/FloatingLabel';
import {
  getFacilityTemplates,
  importFromTemplate,
  getEhrFields,
  getFacilityUsers,
  getFacilityTemplateWorkTypes
} from '../../../Apis/Facility';
import {
  FacilityTemplateCard,
  FacilityTemplatesList,
  StyledForm,
  FormContainer,
  StepIndicator,
  StepTitle,
  ImportedBadge,
  UserListContainer,
  UserListTable,
  UserListHeaderRow,
  UserListHeaderCell,
  UserListRow,
  UserListCell
} from './ImportFromGlobalModalStyles';

const ImportFromGlobalModal = ({ visible, onCancel, onSuccess }) => {
  const [theme] = useSelector(state => [state.themeReducer]);
  const [form] = Form.useForm();

  // State management
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [facilityTemplates, setFacilityTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [facilityUsers, setFacilityUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [noteTypeOptions, setNoteTypeOptions] = useState([]);
  const [workTypeOptions, setWorkTypeOptions] = useState([]);
  const [showRangeForDischargedPatients, setShowRangeForDischargedPatients] =
    useState(false);
  const [importButtonDisabled, setImportButtonDisabled] = useState(true);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const listRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  const fetchIdRef = useRef(0);
  const PAGE_SIZE = 20;

  // Dropdown options
  const patientsRequiredOptions = [
    { value: 'Yes', name: 'Yes' },
    { value: 'No', name: 'No' }
  ];

  // Reset state when modal opens
  useEffect(() => {
    if (visible) {
      setInputValue('');
      setSearchValue('');
      fetchNoteTypeOptions();
      fetchFacilityUsers();
      setStep(1);
      setSelectedTemplate(null);
      setSelectedUsers(new Set());
      setImportButtonDisabled(true);
      form.resetFields();
    }
  }, [visible]);

  // Fetch templates when visible or debounced search value changes
  useEffect(() => {
    if (!visible) return;
    setFacilityTemplates([]);
    setCurrentPage(0);
    setHasMore(true);
    fetchFacilityTemplates(0, true, searchValue);
  }, [visible, searchValue]);

  const handleSearchChange = useCallback(e => {
    const value = e.target.value;
    setInputValue(value);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      setSearchValue(value);
    }, 400);
  }, []);

  const fetchFacilityTemplates = async (page = 0, isInitialLoad = false, search = '') => {
    const fetchId = ++fetchIdRef.current;

    if (isInitialLoad) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }
    try {
      const response = await getFacilityTemplates(page, PAGE_SIZE, search);
      if (fetchId !== fetchIdRef.current) return; // discard stale response
      if (response.data.success) {
        const newTemplates = response.data.result || [];
        const totalSize = response.data.size || 0;

        if (isInitialLoad) {
          setFacilityTemplates(newTemplates);
        } else {
          setFacilityTemplates(prev => [...prev, ...newTemplates]);
        }

        const loadedCount = (page + 1) * PAGE_SIZE;
        setHasMore(loadedCount < totalSize);
        setCurrentPage(page);
      } else {
        notification['error']({
          message: 'Failed to fetch facility templates',
          description: response.data.message
        });
      }
    } catch (err) {
      if (fetchId !== fetchIdRef.current) return;
      console.error('Error fetching facility templates:', err);
      notification['error']({
        message: 'Error',
        description: 'Failed to fetch facility templates'
      });
    } finally {
      if (fetchId === fetchIdRef.current) {
        setLoading(false);
        setLoadingMore(false);
      }
    }
  };

  const fetchNoteTypeOptions = async () => {
    try {
      const response = await getEhrFields(null, 'pointHealth-emr');
      if (response.data.success) {
        const noteTypeField = response.data.result.find(
          field => field.field === 'noteType'
        );
        if (noteTypeField && noteTypeField.value) {
          setNoteTypeOptions(noteTypeField.value);
        }
      }
    } catch (err) {
      console.error('Error fetching note type options:', err);
    }
  };

  const fetchFacilityUsers = async () => {
    try {
      const response = await getFacilityUsers();
      if (response.data.success) {
        setFacilityUsers(response.data.result || []);
      }
    } catch (err) {
      console.error('Error fetching facility users:', err);
      notification['error']({
        message: 'Error',
        description: 'Failed to fetch facility users'
      });
    }
  };

  const handleScroll = useCallback(
    e => {
      const { scrollTop, scrollHeight, clientHeight } = e.target;
      // Load more when user scrolls within 100px of bottom
      if (scrollHeight - scrollTop - clientHeight < 100 && hasMore && !loadingMore && !loading) {
        fetchFacilityTemplates(currentPage + 1, false, searchValue);
      }
    },
    [hasMore, loadingMore, loading, currentPage, searchValue]
  );

  const handleTemplateSelect = template => {
    setSelectedTemplate(template);
    setStep(2);
    getFacilityTemplateWorkTypes(template.id)
      .then(response => {
        if (response.data.success) {
          setWorkTypeOptions(
            (response.data.result || []).map(wt => ({ value: wt, name: wt }))
          );
        }
      })
      .catch(err => {
        console.error('Error fetching work types:', err);
      });
  };

  const toggleUser = userId => {
    setSelectedUsers(prev => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  };

  const handleBack = () => {
    setStep(1);
    setSelectedTemplate(null);
    setShowRangeForDischargedPatients(false);
    setWorkTypeOptions([]);
    setImportButtonDisabled(true);
    setSelectedUsers(new Set());
    form.resetFields();
  };

  const handleImport = async () => {
    try {
      const values = await form.validateFields();

      setImportLoading(true);

      const payload = {
        facilityTemplateId: selectedTemplate.id,
        noteType: values.noteType.changedValue,
        allPatientsRequired: values.allPatientsRequired.changedValue === 'Yes',
        rangeForDischargedPatients: showRangeForDischargedPatients
          ? parseInt(values.rangeForDischargedPatients || 0, 10)
          : 0,
        users: Array.from(selectedUsers).map(userId => ({
          userId,
          selected: true
        })),
        ...(values.workType?.changedValue && {
          workType: values.workType.changedValue
        })
      };

      const response = await importFromTemplate(payload);

      if (response.data.success) {
        notification['success']({
          message: 'Success',
          description: 'Facility imported successfully'
        });
        onSuccess();
        handleClose();
      } else {
        notification['error']({
          message: 'Import Failed',
          description: response.data.message
        });
      }
    } catch (err) {
      if (err.errorFields) {
        // Form validation error
        return;
      }
      console.error('Error importing facility:', err);
      notification['error']({
        message: 'Error',
        description: 'Failed to import facility'
      });
    } finally {
      setImportLoading(false);
    }
  };

  const handleClose = () => {
    setStep(1);
    setSelectedTemplate(null);
    setInputValue('');
    setSearchValue('');
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    setSelectedUsers(new Set());
    setFacilityUsers([]);
    setWorkTypeOptions([]);
    form.resetFields();
    setShowRangeForDischargedPatients(false);
    onCancel();
  };

  const checkFormCompletion = () => {
    const values = form.getFieldsValue(true);
    const requiresRange = values.allPatientsRequired?.changedValue === 'Yes';
    const isComplete = !!(
      values.noteType?.changedValue &&
      values.allPatientsRequired?.changedValue &&
      (!requiresRange || values.rangeForDischargedPatients)
    );
    setImportButtonDisabled(!isComplete);
  };

  const noteTypeChange = value => {
    form.setFieldsValue({ noteType: value });
    checkFormCompletion();
  };

  const allPatientsRequiredChange = value => {
    form.setFieldsValue({ allPatientsRequired: value });
    const showRange = value.changedValue === 'Yes';
    setShowRangeForDischargedPatients(showRange);
    if (showRange) {
      form.setFieldsValue({ rangeForDischargedPatients: '30' });
    } else {
      form.setFieldsValue({ rangeForDischargedPatients: '' });
    }
    checkFormCompletion();
  };

  const rangeForDischargedPatientsChange = e => {
    form.setFieldsValue({ rangeForDischargedPatients: e.target.value });
    checkFormCompletion();
  };

  const renderStepOne = () => (
    <>
      <StepTitle color={theme['@text-color']}>
        Select a facility from the global list
      </StepTitle>
      <Input
        placeholder='Search facilities...'
        prefix={<SearchOutlined />}
        value={inputValue}
        onChange={handleSearchChange}
        allowClear
        onClear={() => {
          setInputValue('');
          if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
          setSearchValue('');
        }}
        style={{ width: '50%', marginBottom: '16px' }}
      />
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Spin size='large' />
        </div>
      ) : facilityTemplates.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <CustomTypography color={theme['@text-color']}>
            No facility templates available
          </CustomTypography>
        </div>
      ) : (
        <FacilityTemplatesList ref={listRef} onScroll={handleScroll}>
          {facilityTemplates.map(template => (
            <FacilityTemplateCard
              key={template.id}
              onClick={() =>
                !template.isImported && handleTemplateSelect(template)
              }
              color={theme['@text-color']}
              disabled={template.isImported}
            >
              {template.isImported && <ImportedBadge>Imported</ImportedBadge>}
              <div
                style={{
                  fontSize: '16px',
                  fontWeight: 600,
                  marginBottom: '8px'
                }}
              >
                {template.facilityName}
              </div>
              {template.ehrConfigForFacilityTemplate && (
                <>
                  <div style={{ fontSize: '14px', marginBottom: '4px' }}>
                    EHR Type:{' '}
                    {template.ehrConfigForFacilityTemplate.ehrType || 'N/A'}
                  </div>
                  {template.ehrConfigForFacilityTemplate.organisationId && (
                    <div style={{ fontSize: '14px', marginBottom: '4px' }}>
                      Organization ID:{' '}
                      {template.ehrConfigForFacilityTemplate.organisationId}
                    </div>
                  )}
                  {template.ehrConfigForFacilityTemplate.facilityId && (
                    <div style={{ fontSize: '14px' }}>
                      Facility ID:{' '}
                      {template.ehrConfigForFacilityTemplate.facilityId}
                    </div>
                  )}
                </>
              )}
            </FacilityTemplateCard>
          ))}
          {loadingMore && (
            <div
              style={{
                gridColumn: '1 / -1',
                textAlign: 'center',
                padding: '20px'
              }}
            >
              <Spin size='small' />
            </div>
          )}
        </FacilityTemplatesList>
      )}
    </>
  );

  const renderStepTwo = () => (
    <Row gutter={24}>
      <Col span={12}>
        <StepTitle color={theme['@text-color']}>
          Configure {selectedTemplate?.facilityName}
        </StepTitle>
        <FormContainer>
          <StyledForm form={form} layout='vertical'>
            <Form.Item
              name='noteType'
              rules={[{ required: true, message: 'Note Type is required' }]}
            >
              <FloatLabel
                label='Note Type'
                primaryColor={theme['@text-color']}
                type='select'
                onChange={noteTypeChange}
                meta={noteTypeOptions}
              />
            </Form.Item>
            <Form.Item
              name='allPatientsRequired'
              rules={[
                { required: true, message: 'List All Patients is required' }
              ]}
            >
              <FloatLabel
                label='List All Patients'
                primaryColor={theme['@text-color']}
                type='select'
                onChange={allPatientsRequiredChange}
                meta={patientsRequiredOptions}
              />
            </Form.Item>
            {showRangeForDischargedPatients && (
              <Form.Item
                name='rangeForDischargedPatients'
                rules={[
                  {
                    required: true,
                    message: 'Get Discharged Patients Within is required'
                  }
                ]}
              >
                <FloatLabel
                  label='Get Discharged Patients Within (in days)'
                  primaryColor={theme['@text-color']}
                  type='input'
                  onChange={rangeForDischargedPatientsChange}
                />
              </Form.Item>
            )}
            <Form.Item name='workType'>
              <FloatLabel
                label='Work Type'
                primaryColor={theme['@text-color']}
                type='select'
                onChange={value => form.setFieldsValue({ workType: value })}
                meta={workTypeOptions}
                required={false}
              />
            </Form.Item>
          </StyledForm>
        </FormContainer>
      </Col>
      <Col span={12}>
        <StepTitle color={theme['@text-color']}>Add Users</StepTitle>
        <UserListContainer>
          <UserListTable>
            <thead>
              <UserListHeaderRow>
                <UserListHeaderCell>User Name</UserListHeaderCell>
                <UserListHeaderCell style={{ width: '80px' }}>
                  Role
                </UserListHeaderCell>
                <UserListHeaderCell
                  style={{ textAlign: 'center', width: '120px' }}
                >
                  Action
                </UserListHeaderCell>
              </UserListHeaderRow>
            </thead>
            <tbody>
              {facilityUsers.length > 0 ? (
                facilityUsers.map(user => (
                  <UserListRow key={user.id}>
                    <UserListCell color={theme['@text-color']}>
                      {user.fullName}
                    </UserListCell>
                    <UserListCell color={theme['@text-color']}>
                      {user.userType}
                    </UserListCell>
                    <UserListCell style={{ textAlign: 'center' }}>
                      <Button
                        type={
                          selectedUsers.has(user.id) ? 'default' : 'primary'
                        }
                        danger={selectedUsers.has(user.id)}
                        size='small'
                        onClick={() => toggleUser(user.id)}
                      >
                        {selectedUsers.has(user.id) ? 'Remove' : 'Add'}
                      </Button>
                    </UserListCell>
                  </UserListRow>
                ))
              ) : (
                <UserListRow>
                  <UserListCell
                    color={theme['@text-color']}
                    colSpan={3}
                    style={{ textAlign: 'center' }}
                  >
                    No users to list
                  </UserListCell>
                </UserListRow>
              )}
            </tbody>
          </UserListTable>
        </UserListContainer>
      </Col>
    </Row>
  );

  return (
    <ConfirmationModal
      visible={visible}
      onCancel={step === 1 ? handleClose : handleBack}
      onOk={handleImport}
      loading={importLoading}
      confirmation={step === 2}
      buttonDisabled={step === 2 && importButtonDisabled}
      cancelText={step === 2 ? 'Back' : undefined}
      okText='Import'
      antdModalProps={{
        centered: true,
        width: 900,
        closable: false
      }}
      header={
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <CustomTypography color={theme['@text-color']}>
            Import Facility From Global List
          </CustomTypography>
          <StepIndicator color={theme['@text-color']}>
            Step {step} of 2
          </StepIndicator>
        </div>
      }
    >
      {step === 1 && renderStepOne()}
      {step === 2 && renderStepTwo()}
    </ConfirmationModal>
  );
};

export default ImportFromGlobalModal;
